import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { Container, Grid, Typography, Box } from "@mui/material";

import Select from "@/components/ph/Select";
import Input from "@/components/ph/Input";
import FormSection from "../../utils/FormSection";

import { resetForm } from "../driverSlice";

import {
  generateDays,
  generateMonths,
  generateYears,
} from "../../contractor/hooks/DateHelpers";

import { useFieldValidation } from "../hooks/useFieldValidation";
import { useZipCodeField } from "../hooks/useZipCodeField";
import { useDateField } from "../hooks/useDateField";

const DriverPersonalData = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(resetForm());
  }, [dispatch]);

  const nombresField = useFieldValidation("nombres", {
    requiredMessage: "El campo es requerido",
    validate: (nombres) => {
      const regex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/;
      if (!regex.test(nombres)) return "El nombre solo puede contener letras y espacios";
      if (nombres.trim().length < 3) return "Debe tener al menos 3 caracteres";
      return "";
    },
    onKeyDown: (e) => {
      const allowedKeys = [
        "Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End", "Enter", " "
      ];
      if (allowedKeys.includes(e.key)) return;
      if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s']$/.test(e.key)) e.preventDefault();
    },
  });

  const generoField = useFieldValidation("genero", {
    requiredMessage: "El campo es requerido",
  });

  const zipCodeField = useZipCodeField();
  const dateField = useDateField();

  return (
    <Container maxWidth="md" disableGutters={{ xs: true, md: false }}>
      <FormSection title="DATOS PERSONALES DEL CONDUCTOR HABITUAL">
        <Grid container spacing={2}>
          {/* Campo Nombres */}
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                minHeight: "3.5rem",
                display: "flex",
                alignItems: "flex-end",
              }}
            >
              <Input
                errorMessage={nombresField.getErrorMessage()}
                isInvalid={nombresField.isInvalid}
                label="Nombre/s"
                name="nombres"
                value={nombresField.value}
                {...nombresField.handlers}
                required
                type="text"
              />
            </Box>
          </Grid>

          {/* Fecha de Nacimiento */}
          <Grid size={{ xs: 12 }}>
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", fontSize: ".8rem" }}
            >
              Fecha de Nacimiento
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 12 }}>
                <Grid container spacing={2} className="fecha-selects">
                  <Grid size={{ xs: 3 }}>
                    <Box
                      sx={{
                        minHeight: "3.5rem",
                        display: "flex",
                        alignItems: "flex-end",
                      }}
                    >
                      <Select
                        errorMessage=""
                        isInvalid={dateField.isDateFieldInvalid("dia")}
                        type="labeled"
                        label="Día"
                        name="dia"
                        {...dateField.fieldHandlers.dia}
                        options={generateDays()}
                        required
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 5 }}>
                    <Box
                      sx={{
                        minHeight: "3.5rem",
                        display: "flex",
                        alignItems: "flex-end",
                      }}
                    >
                      <Select
                        errorMessage=""
                        isInvalid={dateField.isDateFieldInvalid("mes")}
                        type="labeled"
                        label="Mes"
                        name="mes"
                        {...dateField.fieldHandlers.mes}
                        options={generateMonths()}
                        required
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <Box
                      sx={{
                        minHeight: "3.5rem",
                        display: "flex",
                        alignItems: "flex-end",
                      }}
                    >
                      <Select
                        errorMessage=""
                        isInvalid={dateField.isDateFieldInvalid("anio")}
                        type="labeled"
                        label="Año"
                        name="anio"
                        {...dateField.fieldHandlers.anio}
                        options={generateYears()}
                        required
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Typography
                  variant="caption"
                  sx={{
                    color: "error.main",
                    display: "block",
                    mt: "-1.6rem",
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    height: dateField.dateError ? "auto" : "1rem",
                  }}
                >
                  {dateField.dateError || " "}
                </Typography>
              </Grid>

              {/* Código Postal y Género */}
              <Grid size={{ xs: 12, md: 12 }}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid size={{ xs: 6, md: 6 }} className="cp-input">
                    <Box
                      sx={{
                        minHeight: "3.5rem",
                        display: "flex",
                        alignItems: "flex-end",
                      }}
                    >
                      <Input
                        errorMessage={""}
                        isInvalid={zipCodeField.isInvalid}
                        label="Código Postal"
                        name="codigoPostal"
                        value={zipCodeField.value}
                        {...zipCodeField.handlers}
                        required
                        type="text"
                      />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6, md: 6 }} className="genero-select">
                    <Box
                      sx={{
                        minHeight: "3.5rem",
                        display: "flex",
                        alignItems: "flex-end",
                      }}
                    >
                      <Select
                        isInvalid={generoField.isInvalid}
                        errorMessage={generoField.getErrorMessage()}
                        type="labeled"
                        label="Género"
                        name="genero"
                        {...generoField.handlers}
                        options={[
                          { value: "M", label: "Masculino" },
                          { value: "F", label: "Femenino" },
                        ]}
                        required
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </FormSection>
    </Container>
  );
};

export default DriverPersonalData;
